import { useEffect, useState } from "react";
import {
  FileText,
  Send,
  Building2,
  Landmark,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

const steps = [
  {
    id: 0,
    label: "1. Creas la factura",
    icon: FileText,
    color: "#2563EB",
    ms: 4200,
  },
  {
    id: 1,
    label: "2. Se envía al cliente + gestoría",
    icon: Send,
    color: "#0EA5E9",
    ms: 3800,
  },
  {
    id: 2,
    label: "3. El banco cobra y concilia solo",
    icon: Landmark,
    color: "#10B981",
    ms: 4200,
  },
  {
    id: 3,
    label: "4. Todo listo en tu panel",
    icon: CheckCircle2,
    color: "#059669",
    ms: 3800,
  },
];

export default function AnimatedDemo() {
  const [step, setStep] = useState(0);
  const [key, setKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = (step + 1) % steps.length;
      setStep(next);
      setKey((k) => k + 1);
    }, steps[step].ms);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <div className="ml-4 text-xs text-slate-500 font-mono">
          chronovia.es / facturas / nueva
        </div>
      </div>

      {/* Screen content per step */}
      <div className="relative h-[420px] overflow-hidden bg-slate-50">
        {step === 0 && <ScreenNewInvoice keyProp={key} />}
        {step === 1 && <ScreenSending keyProp={key} />}
        {step === 2 && <ScreenBank keyProp={key} />}
        {step === 3 && <ScreenDashboard keyProp={key} />}
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 px-6 py-3 border-t border-slate-200 bg-white">
        {steps.map((s) => {
          const Icon = s.icon;
          const active = s.id === step;
          return (
            <div
              key={s.id}
              className={`flex-1 flex items-center gap-2 transition-opacity duration-500 ${
                active ? "opacity-100" : "opacity-40"
              }`}
            >
              <div
                className={`w-6 h-6 rounded-md grid place-items-center shrink-0 transition-all ${
                  active ? "scale-110" : "scale-90"
                }`}
                style={{
                  background: active ? s.color : "#E2E8F0",
                  color: active ? "white" : "#64748B",
                }}
              >
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="text-[11px] font-medium text-slate-700 truncate hidden sm:block">
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes typeIn {
          from { width: 0; }
          to { width: 100%; }
        }
        @keyframes riseIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes ping-once {
          0% { transform: scale(0.4); opacity: 0; }
          60% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes flyRight {
          0% { transform: translateX(-30px); opacity: 0; }
          80% { transform: translateX(0); opacity: 1; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes progressBar {
          from { width: 0%; }
          to { width: 100%; }
        }
        .anim-rise { animation: riseIn 0.5s ease-out both; }
        .anim-ping { animation: ping-once 0.5s ease-out both; }
        .anim-fly { animation: flyRight 0.7s ease-out both; }
        .anim-progress { animation: progressBar 3s linear both; }
      `}</style>
    </div>
  );
}

/* ----------------------- Screen 1: New invoice ----------------------- */
function ScreenNewInvoice({ keyProp }) {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 900);
    const t2 = setTimeout(() => setPhase(2), 1800);
    const t3 = setTimeout(() => setPhase(3), 2600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [keyProp]);

  return (
    <div key={keyProp} className="p-6 h-full">
      <div className="anim-rise">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
          Nueva factura
        </div>
        <div className="font-display text-xl font-bold text-slate-900 mt-1">
          F-2026-0042 · Estudio Vela SL
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {[
          { d: "Diseño web landing page", q: 1, p: 950 },
          { d: "Sesión de fotos producto", q: 4, p: 120 },
          { d: "Consultoría UX", q: 6, p: 65 },
        ].map((l, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-200 ${
              phase >= i + 1 ? "anim-fly" : "opacity-0"
            }`}
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="w-8 h-8 rounded-md bg-blue-50 grid place-items-center">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">
                {l.d}
              </div>
              <div className="text-xs text-slate-500">
                {l.q} × {l.p.toLocaleString("es-ES")} €
              </div>
            </div>
            <div className="text-sm font-bold text-slate-900">
              {(l.q * l.p).toLocaleString("es-ES")} €
            </div>
          </div>
        ))}
      </div>

      <div
        className={`mt-5 p-4 rounded-lg bg-slate-900 text-white flex items-center justify-between ${
          phase >= 3 ? "anim-ping" : "opacity-0"
        }`}
      >
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">
            Total con IVA 21%
          </div>
          <div className="font-display text-3xl font-bold text-blue-400 mt-1">
            2.045,50 €
          </div>
        </div>
        <div className="text-xs text-slate-400 text-right leading-tight">
          IVA · 355,00 €<br />
          IRPF · -232,50 €
        </div>
      </div>
    </div>
  );
}

/* ----------------------- Screen 2: Sending ----------------------- */
function ScreenSending({ keyProp }) {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1200);
    const t2 = setTimeout(() => setPhase(2), 2400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [keyProp]);

  return (
    <div key={keyProp} className="p-6 h-full">
      <div className="anim-rise text-center">
        <div className="inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
          Envío automático activado
        </div>
        <div className="font-display text-xl font-bold text-slate-900 mt-3">
          Enviando F-2026-0042...
        </div>
      </div>

      <div className="mt-6 space-y-3 max-w-md mx-auto">
        {/* Client email */}
        <div
          className={`flex items-center gap-3 p-4 rounded-lg bg-white border border-slate-200 anim-fly`}
          style={{ animationDelay: "200ms" }}
        >
          <div className="w-10 h-10 rounded-lg bg-blue-100 grid place-items-center shrink-0">
            <Send className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500">Cliente</div>
            <div className="text-sm font-medium text-slate-900 truncate">
              hola@estudiovela.com
            </div>
          </div>
          {phase >= 1 ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600 anim-ping" />
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin" />
          )}
        </div>

        {/* Gestoria email */}
        <div
          className={`flex items-center gap-3 p-4 rounded-lg bg-white border border-slate-200 anim-fly`}
          style={{ animationDelay: "500ms" }}
        >
          <div className="w-10 h-10 rounded-lg bg-indigo-100 grid place-items-center shrink-0">
            <Building2 className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500">Gestoría</div>
            <div className="text-sm font-medium text-slate-900 truncate">
              gestoria@martinez-asesores.es
            </div>
            <div className="text-[10px] text-slate-400">
              PDF + XML Facturae adjuntos
            </div>
          </div>
          {phase >= 2 ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600 anim-ping" />
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin" />
          )}
        </div>

        {phase >= 2 && (
          <div className="text-center anim-rise pt-2">
            <div className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Todo enviado en 2 segundos
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------------- Screen 3: Bank reconciliation ----------------------- */
function ScreenBank({ keyProp }) {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1400);
    const t2 = setTimeout(() => setPhase(2), 2600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [keyProp]);

  return (
    <div key={keyProp} className="p-6 h-full">
      <div className="anim-rise flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-red-600 grid place-items-center text-white font-bold">
          B
        </div>
        <div>
          <div className="font-display font-bold text-slate-900">
            Banco Santander
          </div>
          <div className="text-xs text-slate-500 font-mono">
            ES87 0049 3287 47 8620094732
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <div
          className="flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-200 anim-rise"
          style={{ animationDelay: "200ms" }}
        >
          <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center">
            <FileText className="w-4 h-4 text-slate-500" />
          </div>
          <div className="flex-1 text-sm text-slate-700">Iberdrola</div>
          <div className="text-sm text-slate-700">-78,45 €</div>
        </div>

        <div
          className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
            phase >= 1
              ? "bg-emerald-50 border-emerald-300"
              : "bg-white border-slate-200"
          } transition-all duration-500 relative overflow-hidden`}
        >
          {phase >= 1 && (
            <div
              className="absolute inset-0 bg-emerald-100 opacity-30 anim-progress"
              style={{ mixBlendMode: "multiply" }}
            />
          )}
          <div
            className={`w-8 h-8 rounded-full grid place-items-center transition-colors ${
              phase >= 1 ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"
            }`}
          >
            {phase >= 2 ? (
              <CheckCircle2 className="w-4 h-4 anim-ping" />
            ) : (
              <Landmark className="w-4 h-4" />
            )}
          </div>
          <div className="flex-1 min-w-0 relative">
            <div className="text-sm font-medium text-slate-900 truncate">
              Transferencia recibida · Estudio Vela SL
            </div>
            {phase >= 2 && (
              <div className="text-[11px] text-emerald-700 font-medium anim-rise mt-0.5">
                ✓ Conciliada con F-2026-0042 — marcada como pagada
              </div>
            )}
          </div>
          <div className="text-sm font-bold text-emerald-700 relative">+2.045,50 €</div>
        </div>

        <div
          className="flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-200 anim-rise"
          style={{ animationDelay: "300ms" }}
        >
          <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center">
            <FileText className="w-4 h-4 text-slate-500" />
          </div>
          <div className="flex-1 text-sm text-slate-700">Mercadona</div>
          <div className="text-sm text-slate-700">-45,60 €</div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------- Screen 4: Dashboard summary ----------------------- */
function ScreenDashboard({ keyProp }) {
  return (
    <div key={keyProp} className="p-6 h-full">
      <div className="anim-rise">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
          Todo en tu panel
        </div>
        <div className="font-display text-xl font-bold text-slate-900 mt-1">
          Y sin abrir ni una hoja de Excel
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5">
        {[
          { l: "Ingresos del mes", v: "4.820,50 €", color: "#10B981", delay: 100 },
          { l: "Pendiente cobro", v: "1.180,00 €", color: "#F59E0B", delay: 200 },
          { l: "Saldo bancario", v: "8.947,32 €", color: "#2563EB", delay: 300 },
          { l: "Próximo modelo 303", v: "20 abril", color: "#EF4444", delay: 400 },
        ].map((s, i) => (
          <div
            key={i}
            className="p-4 rounded-lg bg-white border border-slate-200 anim-rise"
            style={{ animationDelay: `${s.delay}ms` }}
          >
            <div className="text-xs text-slate-500">{s.l}</div>
            <div
              className="font-display text-lg font-bold mt-1"
              style={{ color: s.color }}
            >
              {s.v}
            </div>
          </div>
        ))}
      </div>

      <div
        className="mt-4 p-4 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 text-white anim-rise flex items-center justify-between"
        style={{ animationDelay: "600ms" }}
      >
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-100">
            Tiempo ahorrado esta semana
          </div>
          <div className="font-display text-2xl font-bold mt-0.5">4h 20min</div>
        </div>
        <div className="text-4xl">⚡</div>
      </div>
    </div>
  );
}
